/**
 * Business Status Badge Component
 * Displays "Dossier en Cours", "Dossier Signé", "Dossier NO GO", or "Dossier Perdu" badge
 * Can be editable with dropdown to change status
 */
import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { CheckCircle2, XCircle, Clock, ChevronDown, Loader2, FileCheck, Ban } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Determine the display status based on business_status or inferred from admin_status/verdict
const getDisplayStatus = (simulation) => {
  const { business_status, admin_status, verdict } = simulation;
  
  // Explicit business status has priority
  if (business_status === 'signed') return 'signed';
  if (business_status === 'lost') return 'lost';
  if (business_status === 'nogo') return 'nogo';
  if (business_status === 'in_progress') return 'in_progress';
  // Map 'won' to 'signed' for backward compatibility
  if (business_status === 'won') return 'signed';
  
  // Default: En cours
  return 'in_progress';
};

// Status configurations - 4 statuses now
const STATUS_CONFIG = {
  in_progress: {
    bg: 'bg-amber-500 hover:bg-amber-600',
    icon: Clock,
    label: { fr: 'Dossier en Cours', en: 'In Progress' }
  },
  signed: {
    bg: 'bg-emerald-500 hover:bg-emerald-600',
    icon: FileCheck,
    label: { fr: 'Dossier Signé', en: 'Signed' }
  },
  nogo: {
    bg: 'bg-slate-600 hover:bg-slate-700',
    icon: Ban,
    label: { fr: 'Dossier NO GO', en: 'NO GO' }
  },
  lost: {
    bg: 'bg-red-500 hover:bg-red-600',
    icon: XCircle,
    label: { fr: 'Dossier Perdu', en: 'Lost' }
  }
};

export const BusinessStatusBadge = ({ 
  simulation, 
  language = 'fr', 
  editable = false,
  onStatusChange,
  isLoading = false
}) => {
  const [open, setOpen] = useState(false);
  const displayStatus = getDisplayStatus(simulation);
  const config = STATUS_CONFIG[displayStatus];
  const Icon = config.icon;

  // Non-editable version - just display the badge
  if (!editable || !onStatusChange) {
    return (
      <Badge className={`${config.bg} text-white font-semibold gap-1 px-3 py-1`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label[language] || config.label.fr}
      </Badge>
    );
  }

  // Editable version - dropdown to change status
  const handleStatusChange = async (newStatus) => {
    if (newStatus !== displayStatus) {
      await onStatusChange(simulation.id, newStatus);
    }
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Badge 
          className={`${config.bg} text-white font-semibold gap-1 px-3 py-1 cursor-pointer transition-all`}
          data-testid={`business-status-badge-${simulation.id}`}
        >
          {isLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Icon className="w-3.5 h-3.5" />
          )}
          {config.label[language] || config.label.fr}
          <ChevronDown className="w-3 h-3 ml-1" />
        </Badge>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuItem 
          onClick={() => handleStatusChange('in_progress')}
          className={`gap-2 ${displayStatus === 'in_progress' ? 'bg-amber-50' : ''}`}
        >
          <Clock className="w-4 h-4 text-amber-500" />
          <span>{language === 'fr' ? 'Dossier en Cours' : 'In Progress'}</span>
          {displayStatus === 'in_progress' && <CheckCircle2 className="w-4 h-4 ml-auto text-amber-500" />}
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleStatusChange('signed')}
          className={`gap-2 ${displayStatus === 'signed' ? 'bg-emerald-50' : ''}`}
        >
          <FileCheck className="w-4 h-4 text-emerald-500" />
          <span>{language === 'fr' ? 'Dossier Signé' : 'Signed'}</span>
          {displayStatus === 'signed' && <CheckCircle2 className="w-4 h-4 ml-auto text-emerald-500" />}
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleStatusChange('nogo')}
          className={`gap-2 ${displayStatus === 'nogo' ? 'bg-slate-50' : ''}`}
        >
          <Ban className="w-4 h-4 text-slate-600" />
          <span>{language === 'fr' ? 'Dossier NO GO' : 'NO GO'}</span>
          {displayStatus === 'nogo' && <CheckCircle2 className="w-4 h-4 ml-auto text-slate-600" />}
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleStatusChange('lost')}
          className={`gap-2 ${displayStatus === 'lost' ? 'bg-red-50' : ''}`}
        >
          <XCircle className="w-4 h-4 text-red-500" />
          <span>{language === 'fr' ? 'Dossier Perdu' : 'Lost'}</span>
          {displayStatus === 'lost' && <CheckCircle2 className="w-4 h-4 ml-auto text-red-500" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BusinessStatusBadge;
